<template>
  <div>
    <div ref="wrapperEl" class="wrapper" :class="wrapperClasses">
      <slot>
        <div
          v-if="showBadge"
          class="filter-count-badge"
        >
          {{ badgeLabel }}
        </div>
        <OpenDropdownIcon />
        <label
          class="label-placeholder"
          :class="labelClasses"
          :for="name"
        >
          {{ inputLabel }}
        </label>
        <label
          v-if="hasSelectedOperator"
          class="label-selected-options"
        >
          {{ selectedLabel }}
        </label>
        <input
          type="text"
          readonly
          @click="toggleDropdown()"
          @focus="handleFocus"
        />
      </slot>
      <transition name="slide">
        <div ref="optionsEl" class="options" v-if="open">
          <div v-for="option in options"
            :key="option.key"
            :uid="option.key"
            class="option"
            @click.capture="handleOperatorSelect($event, option)"
            :id="generateOptionId(option.value)"
          >
            <Radio
              :key="option.key"
              :name="option.label"
              :value="option.value"
              :selectedValue="option.selectedValue"
              :disabled="option.disabled"
              class="radio"
              :class="radioClasses(option)"
              ref="radioComponent"
            >
              <span>{{ option.label }}</span>
              <span v-if="option.typeText" class="operator-type"> {{ option.typeText }}</span>
            </Radio>
            <div
              class="filters-selection"
            >
              <DropdownBlock
                v-if="showMultiSelect(option)"
                class="subcomponent-input"
                :name="`search-${option.key}`"
                :value="option.filters"
                :dimensionName="dimensionName"
                :isFetching="isFetching"
                @submit="handleSubmit"
              />
            </div>
          </div>
        </div>
      </transition>
    </div>
    <div class="error-message">{{ noValueError }}</div>
  </div>
</template>
<script>
import { OpenDropdownIcon, Radio } from '@mikmak/components';
import DropdownBlock from './RadioButtonDropdownBlock.vue';

export default {
  props: {
    name: {
      type: String,
    },
    value: {
      type: [String, Number, Boolean],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    show: {
      type: Boolean,
    },
    filters: {
      type: Array,
      default() {
        return [
          {
            key: 'Option A',
            value: 'Option A',
            checked: false,
          },
          {
            key: 'Option B',
            value: 'Option B',
            checked: false,
          },
        ];
      },
    },
    dimensionName: {
      type: String,
      default: '',
    },
    isFetching: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: this.show,
      selectedOperator: null,
      missingValueMessage: 'You must select a condition and value',
      options: [
        {
          key: '0',
          label: 'is',
          value: 'is',
          typeText: '(OR)',
          isMultiSelect: true,
          disabled: false,
          filters: [],
        },
        {
          key: '1',
          label: 'is not',
          value: 'isNot',
          typeText: '(OR)',
          isMultiSelect: true,
          disabled: false,
          filters: [],
        },
        {
          key: '2',
          label: 'contains',
          value: 'contains',
          typeText: '(AND)',
          isMultiSelect: true,
          disabled: false,
          filters: [],
        },
        {
          key: '3',
          label: 'does not contain',
          value: 'doesNotContain',
          typeText: '(AND)',
          isMultiSelect: true,
          disabled: false,
          filters: [],
        },
      ],
    };
  },
  components: {
    OpenDropdownIcon,
    Radio,
    DropdownBlock,
  },
  computed: {
    selectedFilters() {
      return this.selectedOperator
        ? this.selectedOperator.filters.filter(f => f.checked)
        : [];
    },
    showBadge() {
      return this.selectedFilters.length > 1;
    },
    hasSelectedOperator() {
      return this.selectedOperator !== null;
    },
    hasSelectedFilters() {
      return this.selectedFilters.length > 0;
    },
    badgeLabel() {
      if (this.selectedFilters.length) {
        return `+ ${this.selectedFilters.length - 1}`;
      }

      return '';
    },
    inputLabel() {
      return this.isFetching && !this.open
        ? 'Loading...'
        : 'Condition and Value';
    },
    labelClasses() {
      return {
        'label-active': !!this.selectedOperator,
        'label-inactive': !this.selectedOperator,
      };
    },
    selectedLabel() {
      let label = '';

      if (this.selectedOperator) {
        label = this.selectedOperator.label;

        if (this.selectedFilters.length) {
          label += ` ${this.selectedFilters[0].key}`;
        }
      }

      return label;
    },
    localVal() {
      if (!this.value) return {};
      const matchingOption = this.options.filter(
        o => o.value === this.value,
      )[0];

      return matchingOption || {};
    },
    wrapperClasses() {
      return {
        active: !this.value,
        open: this.open,
        disabled: this.disabled,
        floating: this.$slots.default,
        error: this.isMissingValue,
      };
    },
    isMissingValue() {
      return !this.isFetching
        && this.required
        && this.selectedFilters.length === 0;
    },
    noValueError() {
      return this.isMissingValue ? this.missingValueMessage : '';
    },
  },
  methods: {
    toggleDropdown() {
      this.open = !this.open;

      if (this.open) {
        this.$emit('updateFiltersIfNeed');
      }
    },
    updateFilters() {
      if (!this.localVal || !this.localVal.value) {
        this.selectedOperator = null;
        this.options.forEach(option => option.filters = this.getResetFilters());
      } else {
        this.options.forEach(option => option.filters = this.getResetFilters());
        const option = this.options.find(option => option.value === this.localVal.value);

        this.handleOperatorSelect(undefined, option);
        this.handleFilterSelect(this.filters);
      }
    },
    getResetFilters() {
      return this.filters.map(filter => {
        const filterCopy = { ... filter };

        filterCopy.checked = false;

        return filterCopy;
      });
    },
    handleFilterSelect(filters) {
      this.selectedOperator.filters = filters;
    },
    handleSubmit(filters) {
      this.handleFilterSelect(filters);
      this.$emit('submit', {
        operator: this.selectedOperator.value,
        filters: this.selectedFilters.map(filter => {
          return filter.value;
        }),
      });
    },
    radioClasses(option) {
      return {
        disabled: option.disabled,
      };
    },
    showMultiSelect(option) {
      return this.selectedOperator
        && this.selectedOperator.isMultiSelect
        && this.selectedOperator.value === option.value;
    },
    showInput(option) {
      return this.selectedOperator
        && this.selectedOperator.isInput
        && this.selectedOperator.value === option.value;
    },
    handleOperatorSelect(event, option) {
      if (option.disabled) {
        return;
      }
      const selectedValue = option.value;

      if (this.selectedOperator && this.selectedOperator.value === selectedValue) {
        return;
      }
      this.options.forEach(op => {
        op.selectedValue = selectedValue;
        op.checked = false;
      });

      this.selectedOperator = option;

      if (event !== undefined) this.handleSubmit(option.filters);

      this.scrollToOption(selectedValue);
    },
    generateOptionId(value) {
      return `${String(value)
        .split(' ')
        .join('-')}-opt`;
    },
    handleBlur() {
      this.$emit('blur', this.value);
      this.open = false;
    },
    handleFocus() {
      this.$emit('focus', this.value);
    },
    externalClickHandler(e) {
      // https://developer.mozilla.org/en-US/docs/Web/API/Node/contains
      // If the click is originating from outside of this component, fire blur event and close.
      if (
        this.$refs.wrapperEl &&
        !this.$refs.wrapperEl.contains(e.target) &&
        this.open
      ) {
        this.handleBlur();
      }
    },
    setUpExternalClickListeners() {
      // using capture phase in case there are any e.stopPropagation calls in the target tree.
      document.addEventListener('click', this.externalClickHandler, true);
      document.addEventListener('touchstart', this.externalClickHandler, true);
    },
    tearDownExternalClickListeners() {
      document.removeEventListener('click', this.externalClickHandler, true);
      document.removeEventListener(
        'touchstart',
        this.externalClickHandler,
        true,
      );
    },
    scrollToOption(value) {
      const element = document.getElementById(
        `${String(value)
          .split(' ')
          .join('-')}-opt`,
      );

      if (element) {
        this.$refs.optionsEl.scrollTop = element.offsetTop;
      }
    },
  },
  watch: {
    filters() {
      this.updateFilters();
    },
    show(newVal) {
      this.open = newVal;
    },
    disabled(newVal, oldVal) {
      if (newVal) {
        this.tearDownExternalClickListeners();
      } else if (newVal !== oldVal) {
        this.setUpExternalClickListeners();
      }
    },
  },
  mounted() {
    this.updateFilters();
  },
};
</script>

<style scoped>
.wrapper {
  display: inline-block;
  width: 100%;
  font-family: Lato;
  position: relative;
  font-size: 16px;
  line-height: 16px;
}
.floating.wrapper {
  display: inline-block;
  width: auto;
}
.wrapper.disabled,
.wrapper.disabled label,
.wrapper.disabled svg {
  pointer-events: none;
  border-color: #c1ccd4;
  color: #c1ccd4;
  fill: #c1ccd4;
}
.wrapper .label-placeholder {
  font-family: Lato;
  cursor: pointer;
  pointer-events: none;
  position: absolute;
  left: 12px;
  color: #778699;
  transform: translate(0, -50%);
  transition: 0.25s ease;
}
.label-inactive {
  top: 50%;
}
.label-active {
  font-size: 12px;
  line-height: 14px;
  transform: translate(0, -100%);
  top: 30%;
}
.label-selected-options {
  font-family: Lato;
  cursor: pointer;
  pointer-events: none;
  position: absolute;
  left: 12px;
  color: #223448;
  left: 12px;
  top: 19px;
  font-size: 16px;
  line-height: 19px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 300px;
  text-align: left;
}
.wrapper input[type="text"] {
  font-family: Lato;
  background: #ffffff;
  border: 1.5px solid #d0dde6;
  color: #223448;
  box-sizing: border-box;
  border-radius: 4px;
  width: 100%;
  height: 44px;
  padding: 0 12px;
  font-size: 16px;
  line-height: 19px;
  transition: border 0.5s ease;
  cursor: pointer;
}
.wrapper.error input[type="text"] {
  border: 1.5px solid #CB4444;
}
.wrapper.open.error input[type="text"] {
  border: 1.5px solid #CB4444;
}
.wrapper.open:not(.error) input[type="text"]{
  border: 1.5px solid #778699;
}
.wrapper input[type="text"]:focus {
  outline: none;
  border-color: #778699;
}
.active input[type="text"] {
  padding-top: 16px;
}
.open input[type="text"] {
  border-radius: 4px 4px 0 0;
}
.wrapper svg {
  position: absolute;
  right: 0;
  margin: 10px;
  cursor: pointer;
  transition: all 0.25s ease;
  pointer-events: none;
}
.wrapper.open svg {
  transform: rotate(-180deg);
}
.options {
  z-index: 2;
  text-align: left;
  background: #ffffff;
  position: absolute;
  border: 0.25px solid #778699;
  border-top: none;
  border-radius: 0 0 4px 4px;
  transform-origin: top;
  transition: transform 0.25s ease-in-out;
  overflow-y: scroll;
  min-width: 200px;
  width: auto;
  min-height: 450px;
  max-height: 650px;
  display: inline-block;
  right: 0;
  left: 0;
  top: 44px;
  z-index: 2;
  padding: 0;
  margin: 0;
}
.floating .options {
  margin-top: 7px;
  border: 0.25px solid #778699;
  box-shadow: 0px 0px 3px rgba(34, 52, 72, 0.4);
  border: none;
  left: inherit;
  border-radius: 4px;
  min-width: 100px;
  max-width: 250px;
}
.option {
  cursor: pointer;
  min-height: 16px;
  padding: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #223448;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.option:hover,
.option.active {
  background: #edf3f7;
}
.option:last-child:hover {
  border-radius: 0 0 4px 4px;
}
.slide-enter,
.slide-leave-to {
  transform: scaleY(0) translateZ(0);
}
.search-input {
  margin: 0;
}
.search-input >>> input[type="text"] {
  border-radius: 4px 4px 0 0;
  border-color: #c1ccd4;
}
.filters-list {
  height: 100%;
  background: #ffffff;
  border: 1.5px solid #c1ccd4;
  border-top: none;
  box-sizing: border-box;
  padding: 14px 15px 4px 15px;
  overflow: auto;
  color: #2c3f58;
}
.buttons {
  background: #ffffff;
  border: 1.5px solid #c1ccd4;
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 14px 15px 4px 15px;
  color: #2c3f58;
  display: flex;
  justify-content: center;
  align-items: center;
}
.subcomponent-input {
  margin-top: 10px;
}
.operator-type {
  color: #778699;
}
.filter-count-badge {
  background-color: #4369F4;
  color: #FFFFFF;
  border-radius: 4px;
  border: 2px solid transparent;
  position: absolute;
  right: 44px;
  padding: 6px 8px 6px 8px;
  margin-top: 7px;
}
.radio {
  cursor: pointer;
}
.radio.disabled span {
  color: #C1CCD4;
}
.error-message {
  color: #9A2F35;
  font-family: Lato;
  font-style: Regular;
  font-size: 12px;
  height: 17px;
}
</style>
